import {
  Chips,
  ContentContainer,
  Meta,
  MetaBox,
  MetaValue,
  MobileHeader,
  LogoContainer,
  Buttons,
  ImageContainer,
} from "@components/atoms/single/Shared"
import { Typography } from "../Typography"
import { ResponsiveImage } from "../ResponsiveImage"
import { Chip } from "../Chip"
import { DocumentCardFragment, ProjectFragment, ResponsiveImageFragment } from "../../../types.graphql"
import { Heading } from "../Heading"
import { Button } from "../Button"
import { VSpace } from "@components/layout/Spacer"
import { format } from "date-fns"
import { cn } from "@utils/tw"
import { forwardRef, ComponentPropsWithoutRef } from "react"

export const DocumentSidebar = ({
  chips,
  document,
  file,
}: {
  chips: {
    slug: string | null
    name: string | null
    uri?: string | null
  }[]
  document: DocumentCardFragment
  file?: {
    mediaItemUrl: string | null
    mediaType: string | null
    mimeType: string | null
    fileSize: number | null
  }
}) => {
  return (
    <MetaBox>
      {document?.featuredImage?.node && (
        <>
          <ResponsiveImage {...document.featuredImage.node} aspect={176 / 248} />
          <VSpace initial={7} />
        </>
      )}

      <Buttons>
        {file?.mediaItemUrl && (
          <Button
            size="large"
            color="yellow"
            icon="DOWNLOAD"
            iconStyle="right"
            href={file.mediaItemUrl}
            target="_blank"
          >
            Download PDF
          </Button>
        )}

        {document.documentMeta?.liveDataLink !== "none" && (
          <Button
            size="large"
            color="black"
            icon="GRAPH"
            iconStyle="right"
            href={
              document.documentMeta?.liveDataLink === "ev"
                ? "/research-data/electric-vehicle/"
                : document.documentMeta?.liveDataLink === "safety"
                  ? "/research-data/road-safety/"
                  : "/research-data/transport-affordability/"
            }
          >
            Interactive data
          </Button>
        )}
      </Buttons>

      <Meta>
        {chips && (
          <MetaValue>
            <Typography as="span" variant="caps">
              Tags
            </Typography>
            <VSpace initial={1} />
            <Chips>
              {chips.map((chip, i) => (
                <Chip color="white" key={i}>
                  {chip.name}
                </Chip>
              ))}
            </Chips>
          </MetaValue>
        )}

        {document?.date && (
          <MetaValue>
            <Typography as="span" variant="caps">
              Publish Date
            </Typography>
            <Typography as="span" variant="text-s">
              {format(document.date, "dd.MM.yyyy")}
            </Typography>
          </MetaValue>
        )}
        {file?.fileSize && (
          <MetaValue>
            <Typography as="span" variant="caps">
              File size
            </Typography>
            <Typography as="span" variant="text-s">
              {((file?.fileSize ?? 0) / 1000000).toFixed(2)}MB
            </Typography>
          </MetaValue>
        )}
        {document?.documentMeta?.dataSources && (
          <MetaValue>
            <Typography as="span" variant="caps">
              Data sources
            </Typography>
            <Typography as="span" variant="text-s">
              {document.documentMeta.dataSources.map((source) => source?.source).join(", ")}
            </Typography>
          </MetaValue>
        )}
      </Meta>
    </MetaBox>
  )
}

export const DocumentMobileHeader = ({
  chips,
  document,
  file,
}: {
  chips: {
    slug: string | null
    name: string | null
    uri?: string | null
  }[]
  document: DocumentCardFragment
  file: {
    mediaItemUrl: string | null
    mediaType: string | null
    mimeType: string | null
    fileSize: number | null
  }
}) => {
  return (
    <MobileHeader>
      <ImageContainer>
        <ResponsiveImage {...document?.featuredImage?.node} aspect={176 / 248} />

        {chips && (
          <Chips>
            {chips.map((chip, i) => (
              <Chip color="white" key={i}>
                {chip.name}
              </Chip>
            ))}
          </Chips>
        )}
      </ImageContainer>

      <ContentContainer>
        <Heading
          as="h1"
          dangerouslySetInnerHTML={{
            __html: `${document.title} <span>${document?.documentMeta?.subtitle ?? ""}</span>`,
          }}
        />

        <Buttons>
          {file.mediaItemUrl && (
            <Button
              size="large"
              color="yellow"
              icon="DOWNLOAD"
              iconStyle="right"
              href={file.mediaItemUrl}
              target="_blank"
            >
              Download PDF
            </Button>
          )}

          {document.documentMeta?.liveDataLink !== "none" && (
            <Button
              size="large"
              color="black"
              icon="GRAPH"
              iconStyle="right"
              href={
                document.documentMeta?.liveDataLink === "ev"
                  ? "/research-data/electric-vehicle/"
                  : document.documentMeta?.liveDataLink === "safety"
                    ? "/research-data/road-safety/"
                    : "/research-data/transport-affordability/"
              }
            >
              Interactive data
            </Button>
          )}
        </Buttons>

        <Meta>
          {document?.date && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Publish Date
              </Typography>
              <Typography as="span" variant="text-s">
                {format(document.date, "dd.MM.yyyy")}
              </Typography>
            </MetaValue>
          )}

          {file?.fileSize && (
            <MetaValue>
              <Typography as="span" variant="caps">
                File size
              </Typography>
              <Typography as="span" variant="text-s">
                {((file?.fileSize ?? 0) / 1000000).toFixed(2)}MB
              </Typography>
            </MetaValue>
          )}

          {document?.documentMeta?.dataSources && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Data sources
              </Typography>
              <Typography as="span" variant="text-s">
                {document.documentMeta.dataSources.map((source) => source?.source).join(", ")}
              </Typography>
            </MetaValue>
          )}
        </Meta>
      </ContentContainer>
    </MobileHeader>
  )
}

export const RsrpSidebar = ({
  chips,
  featuredImage,
  projectMeta,
  file,
  link,
}: {
  chips: {
    slug: string | null
    name: string | null
    uri?: string | null
  }[]
  featuredImage?: ResponsiveImageFragment
  projectMeta: ProjectFragment["projectMeta"]
  file?: {
    mediaItemUrl: string | null
    mediaType: string | null
    mimeType: string | null
    fileSize: number | null
  }
  link?: string
}) => {
  return (
    <MetaBox>
      {projectMeta && (
        <Meta>
          {featuredImage && <ResponsiveImage {...featuredImage} aspect={176 / 248} />}
          {projectMeta.projectDetails?.leads && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Lead research organisation
                {projectMeta.projectDetails.leads.length > 1 ? "s" : ""}
              </Typography>
              {projectMeta.projectDetails.leads.map((lead, k) => (
                <LogoList key={k}>
                  {lead?.logo && (
                    <LogoContainer>
                      <ResponsiveImage {...lead.logo} objectFit="contain" />
                    </LogoContainer>
                  )}
                  <Typography as="span" variant="text-s">
                    {lead?.name}
                  </Typography>
                </LogoList>
              ))}
            </MetaValue>
          )}

          {projectMeta.projectDetails?.additional && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Additional research organisation
                {projectMeta.projectDetails.additional.length > 1 ? "s" : ""}
              </Typography>
              {projectMeta.projectDetails.additional.map((lead, k) => (
                <Typography as="span" variant="text-s" key={k}>
                  {lead?.name}
                </Typography>
              ))}
            </MetaValue>
          )}

          {projectMeta.projectDetails?.leadResearchers && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Lead Researcher{projectMeta.projectDetails.leadResearchers.length > 1 ? "s" : ""}
              </Typography>
              {projectMeta.projectDetails.leadResearchers.map((lead, k) => (
                <Typography as="span" variant="text-s" key={k}>
                  {lead?.name}
                </Typography>
              ))}
            </MetaValue>
          )}

          {projectMeta.projectDetails?.grantAmount && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Grant amount
              </Typography>
              <Typography as="span" variant="text-s">
                {projectMeta.projectDetails.grantAmount}
              </Typography>
            </MetaValue>
          )}

          {projectMeta.projectDetails?.lengthOfProject && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Length of project
              </Typography>
              <Typography as="span" variant="text-s">
                {projectMeta.projectDetails.lengthOfProject}
              </Typography>
            </MetaValue>
          )}

          {projectMeta.projectDetails?.inProgress ? (
            <>
              {projectMeta.projectDetails?.estimatedCompletionDate ? (
                <MetaValue>
                  <Typography as="span" variant="caps">
                    Estimated completion date
                  </Typography>
                  <Typography as="span" variant="text-s">
                    {projectMeta.projectDetails.estimatedCompletionDate}
                  </Typography>
                </MetaValue>
              ) : (
                <MetaValue>
                  <Typography as="span" variant="caps">
                    Current status
                  </Typography>
                  <Typography as="span" variant="text-s">
                    In progress
                  </Typography>
                </MetaValue>
              )}
            </>
          ) : (
            <>
              {projectMeta.projectDetails?.completionDate && (
                <MetaValue>
                  <Typography as="span" variant="caps">
                    Completion date
                  </Typography>
                  <Typography as="span" variant="text-s">
                    {projectMeta.projectDetails.completionDate}
                  </Typography>
                </MetaValue>
              )}
            </>
          )}

          {chips && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Tags
              </Typography>
              <Chips className="relative top-auto left-auto mt-2">
                {chips.map((chip, i) => (
                  <Chip color="white" key={i}>
                    {chip.name}
                  </Chip>
                ))}
              </Chips>
            </MetaValue>
          )}

          <Buttons className="my-0">
            {file?.mediaItemUrl && (
              <Button
                size="large"
                color="yellow"
                icon="DOWNLOAD"
                iconStyle="right"
                href={file.mediaItemUrl}
                target="_blank"
              >
                Download PDF
              </Button>
            )}
            {link && (
              <Button size="large" color="black" icon="ARROW_RIGHT" iconStyle="right" href={link}>
                About the project
              </Button>
            )}
          </Buttons>
        </Meta>
      )}
    </MetaBox>
  )
}

export const RsrpMobileHeader = ({
  title,
  chips,
  featuredImage,
  projectMeta,
  file,
  link,
}: {
  title: string
  chips: {
    slug: string | null
    name: string | null
    uri?: string | null
  }[]
  featuredImage?: ResponsiveImageFragment
  projectMeta: ProjectFragment["projectMeta"]
  file?: {
    mediaItemUrl: string | null
    mediaType: string | null
    mimeType: string | null
    fileSize: number | null
  }
  link?: string
}) => {
  return (
    <MobileHeader>
      <ImageContainer>
        {featuredImage && <ResponsiveImage {...featuredImage} aspect={176 / 248} />}

        {chips && (
          <Chips>
            {chips.map((chip, i) => (
              <Chip color="white" key={i}>
                {chip.name}
              </Chip>
            ))}
          </Chips>
        )}
      </ImageContainer>
      <ContentContainer>
        <Heading
          as="h1"
          dangerouslySetInnerHTML={{
            __html: `${title}`,
          }}
        />

        <Meta className="mt-8">
          {projectMeta?.projectDetails?.leads && (
            <div>
              <Typography as="span" variant="caps">
                Lead research organisation
                {projectMeta.projectDetails.leads.length > 1 ? "s" : ""}
              </Typography>
              {projectMeta.projectDetails.leads.map((lead, k) => (
                <LogoList key={k}>
                  {lead?.logo && (
                    <LogoContainer>
                      <ResponsiveImage {...lead.logo} objectFit="contain" />
                    </LogoContainer>
                  )}
                  <Typography as="span" variant="text-s">
                    {lead?.name}
                  </Typography>
                </LogoList>
              ))}
            </div>
          )}

          {projectMeta?.projectDetails?.additional && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Additional research organisation
                {projectMeta.projectDetails.additional.length > 1 ? "s" : ""}
              </Typography>
              {projectMeta.projectDetails.additional.map((lead, k) => (
                <Typography as="span" variant="text-s" key={k}>
                  {lead?.name}
                </Typography>
              ))}
            </MetaValue>
          )}

          {projectMeta?.projectDetails?.leadResearchers && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Lead Researcher{projectMeta.projectDetails.leadResearchers.length > 1 ? "s" : ""}
              </Typography>
              {projectMeta.projectDetails.leadResearchers.map((lead, k) => (
                <Typography as="span" variant="text-s" key={k}>
                  {lead?.name}
                </Typography>
              ))}
            </MetaValue>
          )}

          {projectMeta?.projectDetails?.grantAmount && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Grant amount
              </Typography>
              <Typography as="span" variant="text-s">
                {projectMeta.projectDetails.grantAmount}
              </Typography>
            </MetaValue>
          )}

          {projectMeta?.projectDetails?.lengthOfProject && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Length of project
              </Typography>
              <Typography as="span" variant="text-s">
                {projectMeta.projectDetails.lengthOfProject}
              </Typography>
            </MetaValue>
          )}

          {projectMeta?.projectDetails?.inProgress ? (
            <>
              {projectMeta.projectDetails?.estimatedCompletionDate ? (
                <MetaValue>
                  <Typography as="span" variant="caps">
                    Estimated completion date
                  </Typography>
                  <Typography as="span" variant="text-s">
                    {projectMeta.projectDetails.estimatedCompletionDate}
                  </Typography>
                </MetaValue>
              ) : (
                <MetaValue>
                  <Typography as="span" variant="caps">
                    Current status
                  </Typography>
                  <Typography as="span" variant="text-s">
                    In progress
                  </Typography>
                </MetaValue>
              )}
            </>
          ) : (
            <>
              {projectMeta?.projectDetails?.completionDate && (
                <MetaValue>
                  <Typography as="span" variant="caps">
                    Completion date
                  </Typography>
                  <Typography as="span" variant="text-s">
                    {projectMeta?.projectDetails.completionDate}
                  </Typography>
                </MetaValue>
              )}
            </>
          )}

          {chips && (
            <MetaValue>
              <Typography as="span" variant="caps">
                Tags
              </Typography>
              <Chips className="relative top-auto left-auto mt-2">
                {chips.map((chip, i) => (
                  <Chip color="grey" key={i}>
                    {chip.name}
                  </Chip>
                ))}
              </Chips>
            </MetaValue>
          )}

          <Buttons className="my-0">
            {file?.mediaItemUrl && (
              <Button
                size="large"
                color="yellow"
                icon="DOWNLOAD"
                iconStyle="right"
                href={file.mediaItemUrl}
                target="_blank"
              >
                Download PDF
              </Button>
            )}
            {link && (
              <Button size="large" color="black" icon="ARROW_RIGHT" iconStyle="right" href={link}>
                About the project
              </Button>
            )}
          </Buttons>
        </Meta>
      </ContentContainer>
    </MobileHeader>
  )
}

export const ToolSidebar = ({
  chips,
  file,
  link,
  toolLink,
  featuredImage,
}: {
  chips: {
    slug: string | null
    name: string | null
    uri?: string | null
  }[]
  file?: {
    mediaItemUrl: string | null
    mediaType: string | null
    mimeType: string | null
    fileSize: number | null
  }
  link?: string
  toolLink?: string | null
  featuredImage?: ResponsiveImageFragment
}) => {
  console.log(featuredImage)
  return (
    <MetaBox>
      {featuredImage && <ResponsiveImage {...featuredImage} aspect={176 / 248} />}
      <Buttons>
        {file?.mediaItemUrl && (
          <Button
            size="large"
            color="yellow"
            icon="DOWNLOAD"
            iconStyle="right"
            href={file.mediaItemUrl}
            target="_blank"
          >
            Download PDF
          </Button>
        )}

        {toolLink && (
          <Button size="large" color="yellow" icon="INFO" iconStyle="right" href={toolLink} target="_blank">
            Discover
          </Button>
        )}

        {link && (
          <Button size="large" color="black" icon="ARROW_RIGHT" iconStyle="right" href={link}>
            About the project
          </Button>
        )}
      </Buttons>

      {chips && (
        <MetaValue>
          <Typography as="span" variant="caps">
            Tags
          </Typography>
          <Chips className="relative top-auto left-auto mt-2">
            {chips.map((chip, i) => (
              <Chip color="white" key={i}>
                {chip.name}
              </Chip>
            ))}
          </Chips>
        </MetaValue>
      )}
    </MetaBox>
  )
}

export const ToolMobileHeader = ({
  title,
  chips,
  file,
  link,
  toolLink,
  featuredImage,
}: {
  title: string
  chips: {
    slug: string | null
    name: string | null
    uri?: string | null
  }[]
  file?: {
    mediaItemUrl: string | null
    mediaType: string | null
    mimeType: string | null
    fileSize: number | null
  }
  link?: string
  toolLink?: string | null
  featuredImage?: ResponsiveImageFragment
}) => {
  return (
    <MobileHeader>
      <ImageContainer>
        {featuredImage && <ResponsiveImage {...featuredImage} aspect={176 / 248} />}

        {chips && (
          <Chips>
            {chips.map((chip, i) => (
              <Chip color="white" key={i}>
                {chip.name}
              </Chip>
            ))}
          </Chips>
        )}
      </ImageContainer>
      <ContentContainer>
        <Heading
          as="h1"
          dangerouslySetInnerHTML={{
            __html: `${title}`,
          }}
        />

        <Meta className="mt-8">
          <Buttons>
            {file?.mediaItemUrl && (
              <Button
                size="large"
                color="yellow"
                icon="DOWNLOAD"
                iconStyle="right"
                href={file.mediaItemUrl}
                target="_blank"
              >
                Download PDF
              </Button>
            )}

            {toolLink && (
              <Button size="large" color="yellow" icon="INFO" iconStyle="right" href={toolLink} target="_blank">
                Discover
              </Button>
            )}

            {link && (
              <Button size="large" color="black" icon="ARROW_RIGHT" iconStyle="right" href={link}>
                About the project
              </Button>
            )}
          </Buttons>
        </Meta>
      </ContentContainer>
    </MobileHeader>
  )
}

const LogoList = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col mb-3", className)} {...props} />
))
LogoList.displayName = "LogoList"
